'use client';

import dynamic from 'next/dynamic';
import type { WidgetProps } from 'components/Widget/types';
import type { ModuleComponentProps } from 'libs/experimentation/moduleRegistry';

// No `loading`: a fallback would make each module its own streamed reveal boundary.

// Only use where the component always server-renders: one first mounted by an interaction
// (a tab switch, an opened panel) holds that whole update until its chunk downloads.

export const CardsVerticalScroll = dynamic<ModuleComponentProps<'CardsVerticalScroll'>>(
  () => import('@/components/CardsVerticalScroll/CardsVerticalScroll'),
);
export const ContentFeatureCarousel = dynamic<ModuleComponentProps<'ContentFeatureCarousel'>>(
  () => import('@/components/ContentFeatureCarousel/ContentFeatureCarousel'),
);
export const CopyAddOnWithButton = dynamic<ModuleComponentProps<'CopyAddOnWithButton'>>(
  () => import('components/CtaAddOn/CtaAddOn'),
);
export const DisclosureAddOn = dynamic<ModuleComponentProps<'DisclosureAddOn'>>(
  () => import('components/DisclosureAddOn/DisclosureAddOn'),
);
export const DisclosureBanner = dynamic<ModuleComponentProps<'DisclosureBanner'>>(
  () => import('components/DisclosureBanner/DisclosureBanner'),
);
export const FeaturedBanner = dynamic<ModuleComponentProps<'FeaturedBanner'>>(
  () => import('components/FeaturedBanner/FeaturedBanner'),
);
export const MapLocator = dynamic<ModuleComponentProps<'MapLocator'>>(() => import('components/MapLocator/MapLocator'));
export const Nav = dynamic<ModuleComponentProps<'Nav'>>(() => import('components/MainNav/Nav'));
export const TabbedMediaCopy = dynamic<ModuleComponentProps<'TabbedMediaCopy'>>(
  () => import('components/TabbedMediaCopy/TabbedMediaCopy'),
);
export const Testimonial = dynamic<ModuleComponentProps<'Testimonial'>>(
  () => import('components/Testimonial/Testimonial'),
);
export const Toast = dynamic<ModuleComponentProps<'Toast'>>(() => import('components/Toast/Toast'));

export const ApySavingsCalculator = dynamic<WidgetProps>(() =>
  import('components/Widget/implementations/ApySavingsCalculator/ApySavingsCalculator').then(
    module => module.ApySavingsCalculator,
  ),
);
export const BudgetCalculator = dynamic<WidgetProps>(() =>
  import('components/Widget/implementations/BudgetCalculator/BudgetCalculator').then(module => module.BudgetCalculator),
);
export const EmergencyFundCalculator = dynamic<WidgetProps>(() =>
  import('components/Widget/implementations/EmergencyFundCalculator/EmergencyFundCalculator').then(
    module => module.EmergencyFundCalculator,
  ),
);
export const LoanCalculator = dynamic<WidgetProps>(() =>
  import('components/Widget/implementations/LoanCalculator/LoanCalculator').then(module => module.LoanCalculator),
);
export const OverdraftCalculator = dynamic<WidgetProps>(() =>
  import('components/Widget/implementations/OverdraftCalculator/OverdraftCalculator').then(
    module => module.OverdraftCalculator,
  ),
);
export const PaycheckCalculator = dynamic<WidgetProps>(() =>
  import('components/Widget/implementations/PaycheckCalculator/PaycheckCalculator').then(
    module => module.PaycheckCalculator,
  ),
);

export const ArticleGridCarousel = dynamic(() => import('components/ArticleGrid/ArticleGridCarousel'));
export const ArticleGridPaginated = dynamic(() => import('components/ArticleGrid/ArticleGridPaginated'));
export const ArticleGridSpotlight = dynamic(() => import('components/ArticleGrid/ArticleGridSpotlight'));
export const ArticleRowCarousel = dynamic(() => import('components/ArticleGrid/ArticleRowCarousel'));
export const BaseCarousel = dynamic(() =>
  import('components/shared/Carousel/BaseCarousel').then(module => module.BaseCarousel),
);
export const BaseModal = dynamic(() => import('components/shared/BaseModal/BaseModal'));
export const BentoBoxCardMobileAccordion = dynamic(() =>
  import('components/BentoBox/BentoBoxCard/BentoBoxCardMobileAccordion').then(
    module => module.BentoBoxCardMobileAccordion,
  ),
);
export const CardCarousel = dynamic(() =>
  import('components/shared/Card/CardCarousel').then(module => module.CardCarousel),
);
export const CardsExpandableItemMobile = dynamic(
  () => import('components/Cards/CardsExpandable/CardsExpandableItemMobile'),
);
export const EmailCapture = dynamic(() => import('components/shared/EmailCapture/EmailCapture'));
export const ExplainerSlider = dynamic(() => import('components/Explainer/ExplainerSlider'));
export const FocusDisclaimers = dynamic(() =>
  import('components/HeroConsolidated/shared/FocusDisclaimers').then(module => module.FocusDisclaimers),
);
export const HeroCareersFilters = dynamic(() =>
  import('components/HeroConsolidated/HeroCareersFilters').then(module => module.HeroCareersFilters),
);
export const HubSpotFormEmbed = dynamic(() =>
  import('components/HubSpot/HubSpotFormEmbed').then(module => module.HubSpotFormEmbed),
);
export const JobList = dynamic(() => import('components/JobList/JobList'));
export const MediaCopyCarousel = dynamic(() =>
  import('components/MediaCopy/MediaCopyCarousel').then(module => module.MediaCopyCarousel),
);
export const PromoCodeInput = dynamic(() => import('components/HeroConsolidated/shared/PromoCodeInput'));
export const ResourceSlider = dynamic(() => import('components/Resources/ResourcesSlider'));
export const SmartBannerClient = dynamic(() =>
  import('components/SmartBanner/SmartBannerClient').then(module => module.SmartBannerClient),
);
export const TeaserLinkCarousel = dynamic(() => import('components/TeaserLinks/TeaserLinkCarousel'));
export const ToggleMenu = dynamic(() => import('components/Faq/ToggleMenu'));
export const VideoVariant = dynamic(() => import('components/CardCarousel/variants/VideoVariant'));
